// Project settings: Details page (name, key, description, icon, color).
// Always-editable form like Jira's Details page. Settings-row layout
// (label-left, content-sized controls, one grouped Save) per docs/ux/14-forms.md.

import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Button, Alert, ErrorAlert } from '@react/components';
import { validateProjectForm, PROJECT_ACCENT_COLORS } from '@/features/project/logic';
import { IconPicker } from '@/components/IconPicker';
import { ColorPicker } from '@/components/ColorPicker';
import { FormRow, AutoGrowTextarea } from '@/components/form';

export const ProjectSettingsDetailsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useProject();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    icon: '',
    color: '#6366f1',
  });

  useEffect(() => {
    if (id && !store.currentItem) {
      store.GetProject(id);
    }
  }, [id]);

  useEffect(() => {
    if (store.currentItem) {
      const item = store.currentItem;
      setFormData({
        name: item.name ?? '',
        key: item.key ?? '',
        description: item.description ?? '',
        icon: item.icon ?? '',
        color: item.color || '#6366f1',
      });
    }
  }, [store.currentItem]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const handleSubmit = async () => {
    if (!id || !store.currentItem) return;

    const validationErrors = validateProjectForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await store.UpdateProject(id, {
        project: {
          ...store.currentItem,
          name: formData.name.trim(),
          key: formData.key.trim(),
          description: formData.description.trim(),
          icon: formData.icon.trim() || undefined,
          color: formData.color || undefined,
        },
        field_mask: { paths: ['name', 'key', 'description', 'icon', 'color'] },
      } as never);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading || store.isLoadingItem) {
    return (
      <div className="max-w-2xl space-y-6">
        <div className="h-7 w-40 rounded bg-muted animate-pulse" />
        <div className="rounded-lg border border-border p-6 animate-pulse space-y-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-24 rounded bg-muted" />
              <div className="h-10 w-full rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!store.currentItem) return null;

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('project.details')}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{t('project.updateInfo')}</p>
      </div>

      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('project.saveSuccess')}
        </Alert>
      )}
      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="rounded-lg border border-border bg-card px-5 py-1">
        <FormRow label={t('project.field.icon')}>
          <div className="flex items-center gap-3">
            <IconPicker value={formData.icon} onChange={(iconId) => handleChange('icon', iconId)} />
            <ColorPicker options={PROJECT_ACCENT_COLORS} value={formData.color} onChange={(v) => handleChange('color', v)} />
          </div>
        </FormRow>

        <FormRow label={t('project.field.name')} htmlFor="project-name">
          <input
            id="project-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder={t('project.field.namePlaceholder')}
            autoCapitalize="off"
            className="w-full max-w-sm rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-destructive">{t(errors.name, { field: t('project.field.name'), min: 2, max: 100 })}</p>
          )}
        </FormRow>

        <FormRow label={t('project.field.key')} htmlFor="project-key" hint={t('project.field.keyHint')}>
          <input
            id="project-key"
            type="text"
            value={formData.key}
            onChange={(e) => handleChange('key', e.target.value.toUpperCase())}
            placeholder={t('project.field.keyPlaceholder')}
            autoCapitalize="off"
            className="w-32 rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.key && <p className="mt-1 text-xs text-destructive">{t(errors.key)}</p>}
        </FormRow>

        <FormRow label={t('project.field.description')} htmlFor="project-description">
          <AutoGrowTextarea
            id="project-description"
            value={formData.description}
            onChange={(v) => handleChange('description', v)}
            placeholder={t('project.field.descriptionPlaceholder')}
            className="rounded-md border border-input bg-background px-3 py-1.5"
          />
        </FormRow>

        <div className="flex items-center justify-end border-t border-border py-3">
          <Button onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting}>
            {t('common.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default ProjectSettingsDetailsPage;
